import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { ChevronRight, MoreHorizontal } from 'lucide-react'

import { cn } from '@/lib/utils'

export type BreadcrumbSegment = {
  label: string
  href?: string
}

type BreadcrumbEntry = {
  id: string
  segments: BreadcrumbSegment[]
}

type BreadcrumbAction =
  | { type: 'set'; entry: BreadcrumbEntry }
  | { type: 'remove'; id: string }

type BreadcrumbContextValue = {
  entries: BreadcrumbEntry[]
  dispatch: React.Dispatch<BreadcrumbAction>
}

const BreadcrumbContext = React.createContext<BreadcrumbContextValue | null>(
  null,
)

type BreadcrumbProps = Omit<React.ComponentProps<'nav'>, 'children'> & {
  segments: BreadcrumbSegment[]
  collapseAt?: number
}

type BreadcrumbBarProps = React.ComponentProps<'div'> & {
  sticky?: boolean
}

function BreadcrumbBar({
  className,
  sticky = true,
  ...props
}: BreadcrumbBarProps) {
  return (
    <div
      data-slot="breadcrumb-bar"
      className={cn(
        'border-border bg-background flex min-h-12 items-center border-b px-6 py-2',
        sticky && 'sticky top-0 z-20',
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbProvider({ children }: React.PropsWithChildren) {
  const [entries, dispatch] = React.useReducer(breadcrumbReducer, [])

  return (
    <BreadcrumbContext.Provider value={{ entries, dispatch }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

function BreadcrumbHost({ segments, ...props }: BreadcrumbProps) {
  const context = React.useContext(BreadcrumbContext)
  const contributedSegments =
    context?.entries.flatMap((entry) => entry.segments) ?? []

  return <Breadcrumb segments={[...segments, ...contributedSegments]} {...props} />
}

function BreadcrumbContribution({
  segments,
}: {
  segments: BreadcrumbSegment[]
}) {
  const context = React.useContext(BreadcrumbContext)
  const dispatch = context?.dispatch
  const id = React.useId()
  const segmentsKey = JSON.stringify(segments)
  const publish = React.useEffectEvent(() => {
    if (!dispatch) return
    dispatch({ type: 'set', entry: { id, segments } })
  })

  React.useLayoutEffect(() => {
    if (!dispatch) return

    publish()
    return () => dispatch({ type: 'remove', id })
  }, [dispatch, id, segmentsKey])

  return null
}

function Breadcrumb({
  className,
  segments,
  collapseAt = 4,
  ...props
}: BreadcrumbProps) {
  const [expanded, setExpanded] = React.useState(false)
  const rendered = expanded
    ? segments.map<RenderedItem>((segment) => ({ kind: 'segment', ...segment }))
    : collapseSegments(segments, collapseAt)

  return (
    <nav
      data-slot="breadcrumb"
      aria-label="Breadcrumb"
      className={cn('text-muted-foreground min-w-0 text-xs/relaxed', className)}
      {...props}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {rendered.map((item, index) => {
          const isLast = index === rendered.length - 1
          const key =
            item.kind === 'ellipsis'
              ? `ellipsis-${index}`
              : `${index}-${item.label}`
          return (
            <li
              key={key}
              data-slot="breadcrumb-item"
              className="inline-flex max-w-full min-w-0 items-center gap-1.5"
            >
              {item.kind === 'ellipsis' ? (
                <button
                  type="button"
                  data-slot="breadcrumb-ellipsis"
                  aria-label="Show all breadcrumb segments"
                  onClick={() => setExpanded(true)}
                  className="hover:text-foreground focus-visible:ring-ring/40 inline-flex items-center rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  <MoreHorizontal className="size-3.5" />
                </button>
              ) : isLast || !item.href ? (
                <span
                  data-slot="breadcrumb-current"
                  aria-current={isLast ? 'page' : undefined}
                  title={item.label}
                  className={cn(
                    'truncate',
                    isLast && 'text-foreground font-medium',
                  )}
                >
                  {item.label}
                </span>
              ) : (
                <Link
                  data-slot="breadcrumb-link"
                  to={item.href}
                  activeOptions={{ exact: true }}
                  title={item.label}
                  className="hover:text-foreground focus-visible:ring-ring/40 truncate rounded-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
                >
                  {item.label}
                </Link>
              )}
              {!isLast && (
                <ChevronRight
                  data-slot="breadcrumb-separator"
                  aria-hidden
                  className="text-muted-foreground/70 size-3 shrink-0"
                />
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

type RenderedItem =
  | ({ kind: 'segment' } & BreadcrumbSegment)
  | { kind: 'ellipsis' }

function collapseSegments(
  segments: BreadcrumbSegment[],
  collapseAt: number,
): RenderedItem[] {
  if (segments.length <= collapseAt) {
    return segments.map((s) => ({ kind: 'segment', ...s }))
  }
  return [
    { kind: 'segment', ...segments[0]! },
    { kind: 'ellipsis' },
    { kind: 'segment', ...segments[segments.length - 2]! },
    { kind: 'segment', ...segments[segments.length - 1]! },
  ]
}

function breadcrumbReducer(
  entries: BreadcrumbEntry[],
  action: BreadcrumbAction,
): BreadcrumbEntry[] {
  if (action.type === 'remove') {
    return entries.filter((entry) => entry.id !== action.id)
  }

  const existingIndex = entries.findIndex(
    (entry) => entry.id === action.entry.id,
  )
  if (existingIndex === -1) return [...entries, action.entry]
  if (segmentsEqual(entries[existingIndex]!.segments, action.entry.segments)) {
    return entries
  }

  return entries.map((entry, index) =>
    index === existingIndex ? action.entry : entry,
  )
}

function segmentsEqual(
  left: BreadcrumbSegment[],
  right: BreadcrumbSegment[],
) {
  return (
    left.length === right.length &&
    left.every(
      (segment, index) =>
        segment.label === right[index]?.label &&
        segment.href === right[index]?.href,
    )
  )
}

export {
  Breadcrumb,
  BreadcrumbBar,
  BreadcrumbContribution,
  BreadcrumbHost,
  BreadcrumbProvider,
}
